import ReactDOM from 'react-dom';
import { nanoid } from 'nanoid';
import { Plugin, TextSelection } from 'prosemirror-state';
import { PluginKey } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';

import { EditorOptions } from '@lyearn/lygo-core';

import {
  getDomNode,
  isInBlockquote,
  isInCallout,
  isInCodeBlock,
  removeMenuContainer,
  setPluginMeta,
} from '../../utils';
import { hideSelectionMenu, isMarkActive } from './commands';
import { SelectionMenu } from './SelectionMenu';
import { MarkType, MarkTypesEnum } from './types';
import { isEmptySelection } from './utils';

export const selectionMenuKey = new PluginKey('selection-menu-plugin');

const SELECTION_MENU_CONTAINER_ID = 'selection-menu-container';
const selectionHandling = (editorView: EditorView) => {
  let props = {
    open: false,
    editorView: editorView,
  };

  const isTextSelection =
    editorView.state.selection instanceof TextSelection && !isEmptySelection(editorView.state);
  const { empty } = editorView.state.selection;
  if (
    isTextSelection &&
    !empty &&
    !isInCodeBlock(editorView.state) &&
    !isInBlockquote(editorView.state) &&
    !isInCallout(editorView.state)
  ) {
    props.open = true;
  }
  setPluginMeta(editorView, selectionMenuKey, props);
  return false;
};

const hideSelectionMenuHandling = (editorView: EditorView) => {
  const { from, to } = editorView.state.selection;
  if (getDomNode(editorView, from) !== getDomNode(editorView, to)) {
    hideSelectionMenu(editorView.state, editorView.dispatch);
  }
  return false;
};

const getSelectionPlugins = (customMarkItems?: MarkType[]) => {
  return ({ portalApi, isLocalizedContent, isMicroLearning }: EditorOptions) => [
    new Plugin({
      key: selectionMenuKey,
      state: {
        init() {
          return { open: false, editorView: null };
        },
        apply(tr, old) {
          const props = tr.getMeta(selectionMenuKey);
          if (props) return props;
          return old;
        },
      },
      view: (editorView) => {
        // This will be called only once. It will add a container div.
        // Selection Menu Button will be rendered in this container div.
        let selectionMenuContainer = document.createElement('div');
        selectionMenuContainer.id = SELECTION_MENU_CONTAINER_ID;
        editorView?.dom?.parentNode?.appendChild(selectionMenuContainer);
        const portalKey = nanoid();

        // Need to return the following functions in the view function
        return {
          update: (view) => {
            let props = selectionMenuKey.getState(view.state);
            const { empty } = view.state.selection;

            if ((props.editorView && empty) || !view.editable) {
              props.open = false;
            }

            if (props.link && isMarkActive(MarkTypesEnum.Link, view.state)) {
              props.open = true;
            }

            if (!view || isMicroLearning) return;

            const dom = view?.dom?.parentNode?.querySelector(`#${SELECTION_MENU_CONTAINER_ID}`);
            if (dom) {
              portalApi.createPortal(
                ReactDOM.createPortal(
                  <SelectionMenu
                    {...props}
                    customMarkItems={customMarkItems}
                    isLocalizedContent={isLocalizedContent}
                  />,
                  dom,
                  portalKey,
                ),
              );
            }
          },
          destroy: () => {
            portalApi.destroyPortal(portalKey);
            removeMenuContainer(SELECTION_MENU_CONTAINER_ID, editorView);
          },
        };
      },
      props: {
        handleDOMEvents: {
          keyup: selectionHandling,
          mouseup: selectionHandling,
          dblclick: hideSelectionMenuHandling,
        },
      },
    }),
  ];
};

export default getSelectionPlugins;
